import { api } from "@/api/client";

// Раздел ФБС Ozon. Разбор и план — docs/ozon-fbs-plan.md.
// Этап A: кабинеты продавца и проверка ключей.

export interface OzonCabinet {
  id: number;
  name: string;
  is_active: boolean;
  position: number;
  /** Подпапка юрлица в Infisical. Ключей здесь нет и не будет — ни открытых,
   *  ни зашифрованных (при `keys_in_portal` они только НА ВХОД, см. payload). */
  infisical_slug: string;
  /** Склады продавца Ozon, у каждого своя комната (склад 1С) — миграция 0162.
   *  Правка — полная замена списка (`warehouses` в payload); старые поля склада
   *  кабинета (`ozon_warehouse_id`, `one_c_warehouse_ref`, …) сервер отбивает 422. */
  warehouses: OzonCabinetWarehouse[];
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string | null;
  kontragent_name: string | null;
  contract_ref: string | null;
  contract_name: string | null;
  sale_kontragent_ref: string | null;
  sale_kontragent_name: string | null;
  sale_contract_ref: string | null;
  sale_contract_name: string | null;
  bank_account_ref: string | null;
  bank_account_name: string | null;
  /** Готовность считает СЕРВЕР — на экране правило не повторяется. */
  keys_ready: boolean;
  keys_error: string;
  /** Ключи вводятся в портал (профиль `db_fernet`), а не лежат в Infisical. */
  keys_in_portal: boolean;
  /** Хватает ли реквизитов 1С для документов продажи. */
  requisites_ready: boolean;
  requisites_error: string;
}

export type OzonCabinetPayload = Partial<
  Omit<
    OzonCabinet,
    "id" | "keys_ready" | "keys_error" | "keys_in_portal" | "requisites_ready" | "requisites_error"
  >
> & {
  name: string;
  /** Ключи открытым текстом — только на вход и только при `keys_in_portal`.
   *  Пусто = «не менять»: сервер их никогда не возвращает, поле всегда пустое. */
  client_id?: string;
  api_key?: string;
};

/** Склад продавца Ozon кабинета со своей комнатой (склад 1С). */
export interface OzonCabinetWarehouse {
  ozon_warehouse_id: number;
  name: string;
  delivery_method_id: number | null;
  delivery_method_name: string;
  one_c_warehouse_ref: string | null;
  one_c_warehouse_name: string | null;
}

/** Склад продавца Ozon вместе с настройками, которые определяют наш процесс. */
export interface OzonWarehouse {
  warehouse_id: number;
  name: string;
  status: string;
  is_rfbs: boolean;
  /** ⚠️ Включена — одноштучные отправления уезжают в «Готов к отгрузке» мимо нас. */
  is_auto_assembly: boolean;
  /** Доверительная приёмка: отгрузка грузовыми местами. */
  has_entrusted_acceptance: boolean;
  /** Формат этикетки — от него зависит её геометрия. */
  carriage_label_type: string;
}

export interface OzonDeliveryMethod {
  id: number;
  name: string;
  status: string;
  warehouse_id: number | null;
}

/** Кабинеты для входа в сбор — доступно кладовщику (админский список требует прав). */
export async function pickCabinets(): Promise<{ id: number; name: string }[]> {
  const { data } = await api.get<{ id: number; name: string }[]>("/ozon-fbs/pick-cabinets");
  return data;
}

export async function listCabinets(): Promise<OzonCabinet[]> {
  const { data } = await api.get<OzonCabinet[]>("/ozon-fbs/cabinets");
  return data;
}

export async function createCabinet(payload: OzonCabinetPayload): Promise<OzonCabinet> {
  const { data } = await api.post<OzonCabinet>("/ozon-fbs/cabinets", payload);
  return data;
}

export async function updateCabinet(
  id: number,
  payload: OzonCabinetPayload,
): Promise<OzonCabinet> {
  const { data } = await api.patch<OzonCabinet>(`/ozon-fbs/cabinets/${id}`, payload);
  return data;
}

export async function deleteCabinet(id: number): Promise<void> {
  await api.delete(`/ozon-fbs/cabinets/${id}`);
}

export async function testCabinet(
  id: number,
): Promise<{ ok: boolean; message: string; warehouses: OzonWarehouse[] }> {
  const { data } = await api.post(`/ozon-fbs/cabinets/${id}/test`);
  return data;
}

export async function cabinetWarehouses(id: number): Promise<OzonWarehouse[]> {
  const { data } = await api.get<OzonWarehouse[]>(`/ozon-fbs/cabinets/${id}/warehouses`);
  return data;
}

export async function cabinetDeliveryMethods(
  id: number,
  warehouseId: number,
): Promise<OzonDeliveryMethod[]> {
  const { data } = await api.get<OzonDeliveryMethod[]>(
    `/ozon-fbs/cabinets/${id}/delivery-methods`,
    { params: { warehouse_id: warehouseId } },
  );
  return data;
}

// --- отправления (этап B) ----------------------------------------------------

export interface OzonPostingItem {
  sku: number;
  offer_id: string;
  name: string;
  quantity: number;
  /** Пусто — SKU не опознан в 1С: такую строку не собрать. */
  nomenclature_ref: string | null;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  needs_mark: boolean;
  /** Сколько штук уже отсканировано в эту строку. */
  picked: number;
  /** Коды этой строки, которые Ozon не принял (`exemplar/status`). */
  mark_errors: OzonMarkError[];
}

/** Не принятый Ozon код штуки. `pick_id` — скан, который снимает «Заменить код». */
export interface OzonMarkError {
  pick_id: string;
  /** Начало кода (GTIN и серийник, без криптохвоста) — по нему ищут штуку. */
  code_head: string;
  error: string;
}

export interface OzonPosting {
  id: string;
  posting_number: string;
  order_number: string;
  status: string;
  substatus: string;
  cutoff_at: string | null;
  cabinet_id: number;
  items: OzonPostingItem[];
  /** Считает сервер: экран не должен складывать штуки сам. */
  units_total: number;
  /** Сколько штук уже собрано. */
  units_picked: number;
  /** Набрано ли отправление целиком — на этап D оно уйдёт только таким. */
  is_complete: boolean;
  /** Собрано в Ozon. Пока пусто — этикетки у площадки НЕ СУЩЕСТВУЕТ. */
  shipped_at: string | null;
  label_printed_at: string | null;
  /** Последний итог проверки кодов в Ozon; `null` — коды не передавались. */
  marks_status: string | null;
  /** Созданные документы 1С: `{kind, number}`. */
  documents: { kind: string; number: string }[];
  has_unresolved: boolean;
}

export interface OzonSyncResult {
  cabinet: string;
  fetched: number;
  created: number;
  updated: number;
  /** Сколько отправлений пропало из выдачи Ozon и погашено. */
  gone: number;
  unresolved_skus: string[];
  error: string;
}

export interface OzonConfig {
  scan_method: "tsd" | "camera" | "both";
  /** Ключи Ozon вводятся в портал — в форме кабинета поля ключей вместо слага. */
  keys_in_portal: boolean;
  /** Есть ли ПТМО в цепочке. Нет — реквизиты передачи в форме не показываем. */
  ptmo: boolean;
}

/**
 * Настройки раздела для экрана сбора. ⚠️ Ручка СВОЯ: конфиг приёмки требует
 * права `receiving`, и кнопка камеры не появилась бы молча.
 */
export async function config(): Promise<OzonConfig> {
  const { data } = await api.get<OzonConfig>("/ozon-fbs/config");
  return data;
}

/**
 * Тело 409 сборки по кодам маркировки: `marks_checking` (Ozon проверяет —
 * повторить), `marks_rejected` (с `problems` по штукам), `marks_blocked`
 * (причина до сети: скан штрихкодом там, где нужен код, ГТД и т.п.),
 * `marks_unknown` (отправление синхронизировано до обновления — обновить).
 */
export interface OzonShipRefusal {
  code: "marks_checking" | "marks_rejected" | "marks_blocked" | "marks_unknown";
  detail: string;
  problems?: { product: string; code_head: string; reason: string }[];
}

/**
 * Одно отправление из нашей базы (в Ozon не ходит). Автосборка перечитывает
 * его после ЛЮБОЙ ошибки `/ship`: ручка может упасть уже после принятой
 * сборки (поставка занята, «уже собирается», таймаут опроса), и решать
 * «собрано ли» надо по статусу, а не по коду ошибки.
 */
export async function getPosting(postingId: string): Promise<OzonPosting> {
  const { data } = await api.get<OzonPosting>(`/ozon-fbs/postings/${postingId}`);
  return data;
}

/** Статусы, при которых отправление собранным НЕ считается — копия
 *  `shipping.NOT_ASSEMBLED` бэка (пусто, ждёт сборки, отменено, не принято). */
const NOT_ASSEMBLED: ReadonlySet<string> = new Set([
  "",
  "awaiting_packaging",
  "cancelled",
  "not_accepted",
]);

/** Подтвердил ли Ozon сборку — по сохранённому статусу, правило
 *  `shipping.is_assembled` бэка (не по `shipped_at`: та отметка — «начали»). */
export function isAssembled(p: Pick<OzonPosting, "status">): boolean {
  return !NOT_ASSEMBLED.has(p.status ?? "");
}

/** Собрать отправление в Ozon. ⚠️ Необратимо: обратной ручки у площадки нет. */
export async function shipPosting(postingId: string): Promise<OzonPosting> {
  const { data } = await api.post<OzonPosting>(`/ozon-fbs/postings/${postingId}/ship`);
  return data;
}

/**
 * Поставка Ozon — кучка собранных отправлений, которые увезут за раз.
 *
 * ⚠️ У самого Ozon такой сущности НЕТ: это НАША учётная единица, на неё
 * выписывается ОДИН комплект документов 1С. Иначе четыре документа
 * приходились бы на каждую коробку — при полусотне отправлений в день это две
 * сотни документов ежедневно.
 */
export interface OzonSupply {
  id: string;
  cabinet_id: number;
  status: "open" | "closed";
  /** Сколько собранных отправлений уедет этой поставкой. */
  postings_total: number;
  /** Сколько в них штук — из них и собираются строки документов. */
  units_total: number;
  settled_at: string | null;
  documents: { kind: string; number: string }[];
  /** Почему выставить нельзя. Считает сервер тем же правилом, что цепочка. */
  blockers: string[];
  /** ПТМО не нужна: продажу целиком покрыл остаток юрлица в комнате. */
  ptmo_skipped: boolean;
}

/** Поставка целиком — для экрана завершения. */
export async function getSupply(supplyId: string): Promise<OzonSupply> {
  const { data } = await api.get<OzonSupply>(`/ozon-fbs/supplies/${supplyId}`);
  return data;
}

/**
 * Выписать ОДИН комплект документов 1С на всю поставку. Повтор ДОВОДИТ
 * начатое, а не дублирует: ссылки созданных документов пишутся до проведения.
 */
export async function createSupplyDocuments(supplyId: string): Promise<OzonSupply> {
  const { data } = await api.post<OzonSupply>(`/ozon-fbs/supplies/${supplyId}/documents`);
  return data;
}

/** Напечатать этикетку на принтере раздела. Повтор безвреден и разрешён. */
export async function printLabel(postingId: string): Promise<OzonPosting> {
  const { data } = await api.post<OzonPosting>(`/ozon-fbs/postings/${postingId}/label`);
  return data;
}

export interface OzonPickRow {
  key: string;
  offer_id: string;
  /** Имя от Ozon — единственное, что есть у товара, не опознанного в 1С. */
  name: string;
  nomenclature_ref: string | null;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  needs_mark: boolean;
  needed: number;
  picked: number;
  total: number;
  /** Сколько лишнего на руках. Отдельно от `needed`: перебор — не «готово». */
  over: number;
  postings: number;
}

/** Строка многотоварного отправления. */
export interface OzonMultiPostingLine {
  offer_id: string;
  /** Имя от Ozon — для строки, не опознанной в 1С. */
  name: string;
  nomenclature_ref: string | null;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  quantity: number;
  picked: number;
  needs_mark: boolean;
}

/** Многотоварное отправление (штук > 1): собирается заходом в него —
 *  скан с `posting_id`, а не из общего списка. */
export interface OzonMultiPosting {
  id: string;
  posting_number: string;
  cutoff_at: string | null;
  units_total: number;
  units_picked: number;
  lines: OzonMultiPostingLine[];
}

export interface OzonPickState {
  /** Товары ОДИНОЧНЫХ отправлений (штука одна) — основной список. */
  rows: OzonPickRow[];
  /** Многотоварные: живые и не собранные, по сроку. */
  multi_postings: OzonMultiPosting[];
  /** `units_*` — весь экран: одиночные + многотоварные. */
  units_total: number;
  units_picked: number;
  /** Считает сервер — тем же правилом, что и `needed` у строк. */
  units_left: number;
  single_units_total: number;
  single_units_picked: number;
  single_units_left: number;
  multi_units_total: number;
  multi_units_picked: number;
  multi_units_left: number;
  /** Бейдж «Разбор (N)» — едет тем же ответом, что и список строк. */
  issues: number;
  /** Открытая поставка кабинета. `null` — собранного ещё нет либо поставку
   *  предложить нельзя, и тогда причина лежит в `supply_note`. */
  supply: OzonSupply | null;
  /** Почему поставки нет, хотя открытые есть (например, у кабинета несколько
   *  складов продавца). Пустое поле без причины экран не отличил бы от «ещё
   *  ничего не собрали». */
  supply_note: string | null;
}

/** Строка «Разбора»: отправление, застрявшее между сборкой и учётом. */
export interface OzonIssue {
  posting_id: string;
  posting_number: string;
  cabinet_id: number;
  /** Машинный код — по нему экран выбирает кнопку повтора. */
  issue: "not_shipped" | "no_label" | "not_attached" | "no_documents" | "blocked";
  /** Поставка ЭТОГО отправления: «Разбор» охватывает кабинет целиком и
   *  показывает застрявшее из прошлых поставок тоже. */
  supply_id: string | null;
  title: string;
  /** Чего не хватает — только у `blocked`, где причин бывает несколько. */
  details: string[];
  units: number;
}

/** «Разбор» — что не доехало. Ручка только показывает; чинят шаги их же ручками. */
export async function listIssues(cabinetId: number): Promise<OzonIssue[]> {
  const { data } = await api.get<OzonIssue[]>("/ozon-fbs/issues", {
    params: { cabinet_id: cabinetId },
  });
  return data;
}

/**
 * Всё состояние экрана сбора одним запросом — как `getPickState` у ВБ.
 * Перечитывается после каждого скана, поэтому второй запрос за тем же
 * удваивал бы задержку на самом горячем пути.
 */
export async function pickState(cabinetId: number): Promise<OzonPickState> {
  const { data } = await api.get<OzonPickState>("/ozon-fbs/pick-state", {
    params: { cabinet_id: cabinetId },
  });
  return data;
}

export interface OzonCabinetOverview {
  cabinet_id: number;
  name: string;
  warehouse_name: string;
  postings: number;
  units_total: number;
  units_picked: number;
  /** Сколько ещё взять. Клампит сервер — перебор не даёт отрицательного. */
  units_left: number;
  ready: number;
  unresolved: number;
  keys_ready: boolean;
  warehouse_ready: boolean;
  /** Открытая поставка кабинета — вход на экран завершения. `null`, если
   *  собранного ещё нет либо открытых поставок несколько. */
  supply_id: string | null;
  /** Сколько собранных отправлений в ней. */
  supply_postings: number;
}

export interface OzonOverview {
  cabinets: OzonCabinetOverview[];
  postings: number;
  units_total: number;
  units_picked: number;
  units_left: number;
}

/**
 * Сводка главного экрана. Читает нашу базу, в Ozon не ходит — поэтому её
 * можно звать на каждом входе, в отличие от «Обновить отправления».
 */
export async function overview(): Promise<OzonOverview> {
  const { data } = await api.get<OzonOverview>("/ozon-fbs/overview");
  return data;
}

export async function listPostings(params: {
  cabinet_id?: number;
}): Promise<{ postings: OzonPosting[]; total: number }> {
  const { data } = await api.get("/ozon-fbs/postings", { params });
  return data;
}

export async function syncPostings(): Promise<{ cabinets: OzonSyncResult[] }> {
  const { data } = await api.post("/ozon-fbs/sync");
  return data;
}

// --- скан (этап C) -----------------------------------------------------------

/** Успех скана либо ВОПРОС. Отказ приходит 409 — см. `OzonScanRefusal`. */
export interface OzonScanResult {
  ok: boolean;
  code: string;
  message: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  partia_name: string;
  /** Отправление, куда лёг скан (`null` у вопроса) — для автосборки и печати. */
  posting_id: string | null;
  posting_number: string;
  item_done: number;
  item_total: number;
  posting_done: number;
  posting_total: number;
  posting_complete: boolean;
  needs_characteristic: boolean;
  needs_partia: boolean;
  characteristic_options: { ref: string; name: string }[];
}

/** Машинные коды отказа скана (409). `in_multi_posting` — товар ждут только
 *  многотоварные отправления: открыть его в блоке ниже. `posting_gone` /
 *  `posting_shipped` — режим отправления, а оно снято Ozon / уже собирается. */
export type OzonScanRefusalCode =
  | "unknown_code"
  | "bad_cim"
  | "duplicate"
  | "box_code"
  | "not_in_posting"
  | "in_multi_posting"
  | "posting_gone"
  | "posting_shipped"
  | "mark_required"
  | "busy"
  | "wrong_base"
  | "barcode_ambiguous";

/** Тело 409: отказ скана с машинным кодом. */
export interface OzonScanRefusal {
  /** Один из `OzonScanRefusalCode`; строка — потому что экран кладёт сюда и
   *  свои коды (`error` — сбой не-409). */
  code: string;
  detail: string;
  nomenclature_name?: string;
  nomenclature_article?: string;
  /** `in_multi_posting`: какие отправления открыть (только видимые по точкам,
   *  в порядке, в каком их называет текст). */
  postings?: { id: string; posting_number: string }[];
}

export interface OzonScanPayload {
  /** Ответ «принять без партии»: партии у товара нет — это законное значение. */
  accept_without_partia?: boolean;
  cabinet_id: number;
  scan_kind: string;
  raw: string;
  ean13?: string;
  gtin?: string;
  serial?: string;
  batch?: string;
  characteristic_ref?: string;
  /** Режим отправления: скан только в строки этого отправления. Нет — общий
   *  список, кандидаты только одиночные. */
  posting_id?: string;
}

export async function scan(payload: OzonScanPayload): Promise<OzonScanResult> {
  const { data } = await api.post<OzonScanResult>("/ozon-fbs/scan", payload);
  return data;
}

export async function deletePick(id: string): Promise<void> {
  await api.delete(`/ozon-fbs/picks/${id}`);
}
